import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { audiencesApi } from '../../../api/audiences';
import ChainList from '../../../components/base/ChainLogo/ChainList';
import Loader from '../../../components/base/Loader';
import Table from '../../../components/base/Table';
import tableStyles from '../../../components/base/Table/Table.module.scss';
import Export from '../../../components/ui/Buttons/Export';
import ItemPreview from '../../../components/ui/ItemPreview';
import ConfirmModal from '../../../components/ui/modals/ConfirmModal';
import DownloadModal from '../../../components/ui/modals/DownloadModal';
import AgeDate from '../../../components/ui/Table/AgeDate';
import Header from '../../../components/ui/Table/Header';
import Tooltip from '../../../components/ui/Tooltip';
import { setSelectedAsset } from '../../../store/reducers/app';
import { counter } from '../../../tools/Counter';
import { convertToReadableFormat } from '../../../tools/NumberConverterTool';
import useCancelableRequest from '../../../utils/cancelAPI/useCancelableRequest';
import { supportedBlockchains } from '../../../utils/supportedBlockchains';
import { aliasData, aliasTooltipInfo } from '../../../utils/wallets';
import ProfileSnapshot from './ProfileSnapshot';
import styles from './WalletList.module.scss';

const MemoizedTable = React.memo(Table);

const WalletList = ({ setShowDialog, userSegment }) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [field, setField] = useState('total_portfolio_value');
  const prevFieldRef = useRef('total_portfolio_value');
  const [page, setPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [desc, setDesc] = useState(true);
  const [limit, setLimit] = useState(25);
  const [tableData, setTableData] = useState([]);

  const setFieldKeepingPrev = (value) => {
    prevFieldRef.current = field;
    setField(value);
  };

  const {
    data: walletsData,
    isLoading: isWalletsDataLoading,
    refetch: walletsDataRefetch,
  } = useCancelableRequest({
    endpoint: audiencesApi.endpoints.getSegmentWalletsData,
    params: {
      body: {
        userSegment,
        limit,
        offset: (page - 1) * limit,
        sort: desc ? 'desc' : 'asc',
        sortBy: field,
      },
    },
  });

  const [getWalletCSV, result] = audiencesApi.useGetWalletsCSVMutation();
  const [csvWallets, csvWalletsResult] =
    audiencesApi.useDownloadSegmentWalletsCSVMutation();

  const downloading = () => {
    setShowDialog(false);
    getWalletCSV({
      id,
      wait: true,
    });
  };

  const header = [
    {
      id: 1,
      field: 'address_name',
      title: 'Address Name',
    },
    {
      id: 2,
      field: '',
      title: 'Alias',
    },
    {
      id: 2,
      field: 'total_portfolio_value',
      title: 'Portfolio Value',
    },
    {
      id: 3,
      field: 'first_txn_timestamp',
      title: 'Wallet Age',
    },
    {
      id: 4,
      field: '',
      title: <div className="d-flex justify-content-center">Active On</div>,
    },
  ];

  useEffect(() => {
    if (walletsData?.profile_data) {
      const temp = walletsData.profile_data.map((elem, index) => ({
        address_name: (
          <>
            <span
              className={`d-block ${tableStyles.underline_on_hover}`}
              data-for={`Name_${elem.ALID || elem.address}`}
              data-tip
            >
              <Link
                className="disable-link-styles"
                to={`/profile/${elem.ALID || elem.address}`}
              >
                <div
                  role="presentation"
                  className={`${styles.name} text-truncate`}
                  onClick={() => dispatch(setSelectedAsset({}))}
                >
                  {elem.address_name || elem.address}
                </div>
              </Link>
            </span>
            <ItemPreview
              id={`Name_${elem.ALID || elem.address}`}
              data={{
                id: elem.address,
                alid: elem.ALID || '',
                name: elem.address_name || elem.address,
                type: 'wallet',
              }}
            />
          </>
        ),
        alias: (
          <div className="d-flex gap-2 align-items-baseline">
            {elem?.alias ? (
              <>
                <span data-for={`alias ${index}`} data-tip>
                  {aliasData[elem.alias_type]}
                </span>
                <Tooltip
                  info={aliasTooltipInfo[elem.alias_type]}
                  id={`alias ${index}`}
                  place="top"
                />
                <span>{elem.alias}</span>
              </>
            ) : (
              '-'
            )}
          </div>
        ),
        total_portfolio_value: `${
          elem.total_portfolio_value < 0
            ? '?'
            : elem.total_portfolio_value !== null
              ? `${convertToReadableFormat(elem.total_portfolio_value)} USD`
              : '0 USD'
        }`,
        first_txn_timestamp: (
          <AgeDate
            date={elem.first_txn_timestamp}
            defaultText="No Transactions"
            isAge
          />
        ),
        chain: (
          <div className={styles.chains}>
            <ChainList chains={supportedBlockchains(elem.active_on)} small />
          </div>
        ),
      }));
      setTableData(temp);
    }
  }, [walletsData, dispatch]);

  useEffect(() => {
    walletsDataRefetch();
  }, []);

  useEffect(() => {
    if (prevFieldRef.current !== field) {
      walletsDataRefetch();
    }
  }, [field]);

  useEffect(() => {
    if (result.isLoading || csvWalletsResult.isLoading) {
      setIsOpen(true);
    }
    if (
      (result.isSuccess && result.data) ||
      (csvWalletsResult.isSuccess && csvWalletsResult.data)
    ) {
      const handleOpen = () => {
        setTimeout(() => {
          if (result.isSuccess && result.data) {
            window.location.replace(result.data[0].link);
          } else {
            window.location.replace(csvWalletsResult.data[0].link);
          }
          setShowDialog(true);
        }, 1000);
      };
      handleOpen();
    }
    if (csvWalletsResult.error?.status === 403) {
      setIsOpen(false);
    }
    return () => clearTimeout();
  }, [csvWalletsResult, result, setShowDialog]);

  return (
    <div className={styles.wrapper}>
      {isWalletsDataLoading && !tableData.length ? (
        <Loader />
      ) : (
        <>
          <Header
            title="Audience profiles"
            info={counter(walletsData?.profile_count, page, limit)}
            actions={
              <>
                {id ? (
                  <Export
                    tooltip="Export Profile List to CSV"
                    tooltipWarnings={[
                      'No more than 50,000 profiles will be downloaded',
                      'Make sure the latest segment changes are saved to get up-to-date data',
                    ]}
                    onClick={() => downloading()}
                  />
                ) : (
                  <Export
                    tooltip="Export Profile List to CSV"
                    tooltipWarnings={[
                      'No more than 50,000 profiles will be downloaded',
                    ]}
                    onClick={() => {
                      setShowDialog(false);
                      csvWallets(userSegment);
                    }}
                  />
                )}
                <ProfileSnapshot
                  segment={userSegment}
                  number={walletsData?.profile_count}
                />
              </>
            }
          />
          <MemoizedTable
            total={walletsData?.profile_count}
            data={tableData}
            desc={desc}
            setDesc={setDesc}
            ordering={field}
            setOrdering={setFieldKeepingPrev}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            headers={header}
            isLoading={isWalletsDataLoading}
            min={25}
          />
          {isOpen && csvWalletsResult?.error?.status !== 403 ? (
            <DownloadModal
              result={
                result.status !== 'uninitialized' ? result : csvWalletsResult
              }
              setIsOpen={setIsOpen}
            />
          ) : null}
          {csvWalletsResult?.error?.status === 403 ? (
            <ConfirmModal
              title="We are sorry..."
              description="The feature you’re trying to use has restricted access.
                 Please reach out to your Customer Success Manager."
              buttonName="Okay"
              onSubmit={() => csvWalletsResult.reset()}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default WalletList;
